import { useMemo } from 'react';

interface DetailedStandard {
  _id: string;
  teachingStandardName?: string;
  [key: string]: any;
}

/**
 * Custom hook to filter detailed standards by teaching standard name.
 * 
 * @param standards - Array of detailed standards to filter
 * @param teachingStandardName - The teaching standard name to filter by (e.g., "TEKS", "Common Core State Standards")
 * @param componentName - Name of component for logging purposes
 * @returns Filtered array of standards
 */
export const useFilterStandardsByTeaching = <T extends DetailedStandard>(
  standards: T[],
  teachingStandardName: string | null,
  componentName: string = 'Component'
): T[] => {
  return useMemo(() => {
    if (!standards || standards.length === 0) {
      return [];
    }
    
    // If no teachingStandardName is specified, return all standards
    if (!teachingStandardName) {
      console.log(`⚠️ [${componentName}] No teachingStandard specified, returning all standards`);
      return standards;
    }
    
    // Check if any standards have teachingStandardName populated
    const standardsWithName = standards.filter(s => s.teachingStandardName);
    const standardsWithoutName = standards.filter(s => !s.teachingStandardName);
    
    console.log(`📊 [${componentName}] Standards breakdown:`, {
      total: standards.length,
      withName: standardsWithName.length,
      withoutName: standardsWithoutName.length,
      filteringBy: teachingStandardName
    });
    
    // Debug: Show sample of standard names
    if (standardsWithName.length > 0) {
      const sampleNames = [...new Set(standardsWithName.slice(0, 5).map(s => s.teachingStandardName))];
      console.log(`📋 [${componentName}] Sample standard names found:`, sampleNames);
    }
    
    // If no standards have teachingStandardName, return all (backward compatibility)
    if (standardsWithName.length === 0) {
      console.warn(`⚠️ [${componentName}] No standards have teachingStandardName populated, returning all standards`);
      return standards;
    }
    
    // Filter standards where teachingStandardName matches
    const filtered = standards.filter(standard => {
      const stdName = standard.teachingStandardName;
      
      // If teachingStandardName is not available, EXCLUDE it when filtering
      // (Only include standards without name if we're not filtering at all)
      if (!stdName) {
        return false; // Exclude standards without teachingStandardName when filtering
      }
      
      const stdNameUpper = String(stdName).toUpperCase();
      const filterNameUpper = teachingStandardName.toUpperCase();
      
      // Direct match
      if (stdNameUpper === filterNameUpper) {
        return true;
      }
      
      // Handle variations: "TEKS", "Texas TEKS", "CCSS", "Common Core State Standards", etc.
      // Only match if BOTH contain the same standard system
      if (filterNameUpper.includes('TEKS')) {
        // If filtering by TEKS, only include TEKS standards
        return stdNameUpper.includes('TEKS');
      }
      
      if (filterNameUpper.includes('COMMON CORE') || filterNameUpper.includes('CCSS')) {
        // If filtering by Common Core, only include Common Core standards
        return stdNameUpper.includes('COMMON CORE') || stdNameUpper.includes('CCSS');
      }
      
      // For other standard systems, use exact or contained match
      // But be more strict - only match if the filter name is contained in the standard name
      // This prevents "Common Core" from matching something that just contains "Core"
      return stdNameUpper.includes(filterNameUpper);
    });
    
    console.log(`✅ [${componentName}] Filtered standards:`, {
      before: standards.length,
      after: filtered.length,
      teachingStandardName: teachingStandardName
    });
    
    return filtered;
  }, [standards, teachingStandardName, componentName]);
};

